<template>
  <div>
    <h1 class="text-center my-2">Districts</h1>
    <add-or-update-district-form
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData" @resetUpdateData="resetUpdateData"
    />

    <list-table :records="records" :columns="tableColumns" :total-records="totalRecords" :search-filter.sync="search"
      :current-page-number.sync="currentPage" :show-search="true" :show-filter-by-default="true"
    >

      <template #filters>
        <b-row>
          <b-col cols="12" md="2" class="">
            <v-select
              v-model="areaFilter"
              placeholder="Select Area"
              :loading="loading"
              :clearable="true"
              :options="areaOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @search="onSearchArea"
            />
          </b-col>
          <b-col cols="12" md="2" class="">
            <v-select
              v-model="missionZoneFilter"
              placeholder="Is Mission Zone?"
              :loading="loading"
              :clearable="true"
              :options="yesNoOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @search="onSearchArea"
            />
          </b-col>
          <b-col cols="12" md="2" class="">
            <v-select
              v-model="pwicFilter"
              placeholder="Is PWIC?"
              :loading="loading"
              :clearable="true"
              :options="yesNoOptions"
              :reduce="(val) => val.value"
              :searchable="true"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              @search="onSearchArea"
            />
          </b-col>
        </b-row>
      </template>
      <template v-if="can('districts:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add district</span>
        </b-button>
      </template>

      <template #cell(parent_type)="data">
        <b-badge
          pill
          :variant="`light-${getValueFromSource(data.item, 'area.region_id') ? 'primary' : 'info'}`"
          class="text-capitalize"
        >
          {{ getValueFromSource(data.item, 'area.region_id') ? 'Region (Ghana)' : 'Country (Mission)' }}
        </b-badge>
      </template>

      <template #cell(mission_zone)="data">
        <b-badge pill :variant="`light-${resolveStatusVariant(data.item.mission_zone ? 'yes' : 'no')}`"
          class="text-capitalize"
>
          {{ data.item.mission_zone ? "YES" : "NO" }}
        </b-badge>
      </template>

      <template #cell(is_pwic)="data">
        <b-badge pill :variant="`light-${resolveStatusVariant(data.item.is_pwic ? 'yes' : 'no')}`"
          class="text-capitalize"
>
          {{ data.item.is_pwic ? "YES" : "NO" }}
        </b-badge>
      </template>

      <template #cell(actions)="data">
        <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
          </template>

          <b-dropdown-item v-if="can('districts:CanUpdate')" @click="toggleMissionZone(data.item)">
            <feather-icon icon="MapPinIcon" />
            <span class="align-middle ml-50">
              {{ !data.item.mission_zone ? 'Mark as Mission Zone' : 'Unmark as Mission Zone' }}
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('districts:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">
              Edit
            </span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('districts:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="TrashIcon" />
            <span class="align-middle ml-50">
              Delete
            </span>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateDistrictForm from '@/@core/components/shared/geo/AddOrUpdateDistrictForm.vue';

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import { BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge, BDropdown, BDropdownItem } from "bootstrap-vue";

const watchHandler = {
  handler() {
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateDistrictForm,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],
      areaOptions: [],
      areaFilter: null,
      missionZoneFilter: null,
      pwicFilter: null,

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: 'title', label: "District", sortable: false },
        {
          key: 'parent_type',
          label: 'Belongs To',
          sortable: false,
          formatter: (_, __, item) => (get(item, 'area.region_id') ? 'Region (Ghana)' : 'Country (Mission)'),
        },
        { key: 'mission_zone', label: 'Mission Zone', sortable: false },
        { key: 'is_pwic', label: 'PWIC', sortable: false },
        { key: 'area.title', label: "Area", sortable: false, formatter: val => val || '-' },
        {
          key: 'area.region.title',
          label: 'Region / RMC',
          sortable: false,
          formatter: (value, _, item) => {
            if (get(item, 'area.region_id')) {
              return value || '-'
            }
            return get(item, 'area.country.rmc.title') || 'No RMC Assigned'
          },
        },
        { key: 'area.country.country', label: 'Country', sortable: false, formatter: val => val || '-' },
        { key: 'created', sortable: false, formatter: val => `${formatDate(val, 'dd-MM-yy h:m:s a')}`, label: 'Date', class: 'width-200' },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get() {
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
    areaFilter: watchHandlerRequiresPageReset,
    missionZoneFilter: watchHandlerRequiresPageReset,
    pwicFilter: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search,
      page = 1,
      limit = this.perPage,
      area_id,
      mission_zone,
      is_pwic,
    } = this.$route.query;

    if (area_id) {
      this.areaFilter = area_id
      this.addSingleAreaOption(area_id)
    }

    if (mission_zone) {
      this.missionZoneFilter = mission_zone
    }

    if (is_pwic) {
      this.pwicFilter = is_pwic
    }

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          search: this.search,
          page: this.currentPage,
        }

        if (this.areaFilter) {
          query.area_id = this.areaFilter
        }

        if (this.missionZoneFilter) {
          query.mission_zone = this.missionZoneFilter
        }

        if (this.pwicFilter) {
          query.is_pwic = this.pwicFilter
        }

        await this.$router.push({ query }).catch(() => { });

        const request = await this.useJwt().sharedService.fetchGeoDistricts(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.deleteGeoDistrict(id)

        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message

        this.$nextTick(() => {
          this.toast({
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'error',
            text: error_message
          });
        })
      } finally {
        this.loading = false;
      }
    },
    async toggleMissionZone(district) {
      try {
        this.loading = true;

        const mark = district.mission_zone ? "mark" : "unmark";

        const result = await this.$swal({
          title: 'Confirm Mission Zone Update?',
          text: `Do you want to ${mark} this district as a mission zone?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().sharedService.updateGeoDistrict(district.id, { mission_zone: !district.mission_zone });
        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'District updated successfully.'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update) {
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData() {
      if (this.updateData) {
        this.updateData = null
      }
    },
    async onSearchArea(search, loading) {
      try {
        loading(true);

        if (!search) {
          return;
        }

        const query = {
          limit: this.perPage,
          search,
        }

        const request = await this.useJwt().sharedService.fetchGeoAreas(query);
        const { data } = request.data;

        this.areaOptions = data.map((item) => ({
          label: item.title,
          value: item._id 
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        loading(false);
      }
    },
    async addSingleAreaOption(id) {
      try {
        const request = await this.useJwt().sharedService.getGeoArea(id);
        const { data } = request.data;

        this.areaOptions = [{
          label: data.title,
          value: data._id 
        }]
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message || "Error fetching area";
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      }
    },
  }
}
</script>
